import { useCallback, useEffect, useRef, useState } from 'react';
import { getTronEnergyOrders, type TronEnergyOrder, type TronEnergyOrderStatus } from '@/api';

export interface UseTronEnergyOrdersPollingProps {
  appId: string;
  pollInterval?: number;
}

export function useTronEnergyOrdersPolling({
  appId,
  pollInterval = 3000,
}: UseTronEnergyOrdersPollingProps) {
  const [orders, setOrders] = useState<TronEnergyOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [status, setStatus] = useState<TronEnergyOrderStatus | ''>('');
  const [keyword, setKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isPollingError, setIsPollingError] = useState(false);

  const isMountedRef = useRef(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 获取订单数据
  const fetchOrders = useCallback(
    async (isManualTrigger = false) => {
      if (!appId) return;
      if (isManualTrigger) {
        setIsLoading(true);
      }

      try {
        const res = await getTronEnergyOrders({
          appId,
          status,
          keyword,
          page,
          limit,
        });

        if (!isMountedRef.current) return;

        if (res.ok) {
          setOrders(res.data.items);
          setTotal(res.data.total);
          setIsPollingError(false);
        } else {
          setIsPollingError(true);
        }
      } catch (err) {
        if (!isMountedRef.current) return;
        console.error('[TronEnergyOrdersPolling] Polling error:', err);
        setIsPollingError(true);
      } finally {
        if (isMountedRef.current && isManualTrigger) {
          setIsLoading(false);
        }
      }
    },
    [appId, status, keyword, page, limit],
  );

  // 初始与参数变化时触发查询
  useEffect(() => {
    isMountedRef.current = true;
    fetchOrders(true);

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchOrders]);

  // 定时轮询与页面可见性监听
  useEffect(() => {
    const startPolling = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchOrders(false);
        }
      }, pollInterval);
    };

    const stopPolling = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchOrders(false);
        startPolling();
      } else {
        stopPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    startPolling();

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchOrders, pollInterval]);

  const refreshImmediately = useCallback(() => {
    return fetchOrders(true);
  }, [fetchOrders]);

  return {
    orders,
    total,
    page,
    limit,
    status,
    keyword,
    isLoading,
    isPollingError,
    setPage,
    setLimit,
    setStatus,
    setKeyword,
    refreshImmediately,
  };
}
